// Exporta a capa do projeto para CSV (abre no Excel), incluindo o estado do
// projeto, a etapa atual e a previsão de lançamento.
import { LINE_STATUS_LABEL, CATEGORY_LABELS, PRIORITY_LABELS } from '../components/ui';
import type { ProductCard, FlavorRow } from './capa';
import type { Product, ProductSummary } from '../shared/types';

const MESES = ['M1', 'M2', 'M3', 'M4', 'M5', 'M6', 'M7', 'M8', 'M9', 'M10', 'M11', 'M12'];
const LIFE_PT: Record<string, string> = {
  ativo: 'Ativo',
  pausado: 'Em espera',
  concluido: 'Concluído',
  cancelado: 'Cancelado',
};

function csvCell(v: unknown): string {
  const s = v == null ? '' : String(v);
  return /[";\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}
const row = (cells: unknown[]) => cells.map(csvCell).join(';');

export function exportCapaCSV(
  product: Product,
  summary: ProductSummary | null,
  card: ProductCard,
  flavors: FlavorRow[],
): void {
  const lines: string[] = [];
  lines.push(row(['Capa do Projeto — Workflow de Novos Produtos · Trevo Lácteos']));
  lines.push(row(['Projeto', product.name]));
  lines.push(row(['Marca', product.brand]));
  lines.push(row(['Prioridade', PRIORITY_LABELS[product.priority ?? 'media']]));
  lines.push(row(['Categoria', CATEGORY_LABELS[product.category ?? 'inovacao']]));
  lines.push(row(['Estado do projeto', LIFE_PT[product.lifecycle ?? 'ativo']]));
  if (summary) {
    lines.push(row(['Situação (saúde)', LINE_STATUS_LABEL[summary.status]]));
    lines.push(row(['Etapa/Fase atual', summary.faseAtual]));
    lines.push(row(['Concluído (%)', `${Math.round(summary.percentConcluido * 100)}%`]));
    lines.push(row(['Previsão de lançamento', fmt(summary.previsaoConclusao)]));
    if (summary.targetLaunch) {
      lines.push(row(['Meta de lançamento', fmt(summary.targetLaunch)]));
      lines.push(row(['Desvio vs. meta (dias)', summary.desvioMeta ?? '']));
    }
  }
  lines.push('');
  lines.push(row(['Projeção de Vendas — por sabor (kg/mês)']));
  lines.push(
    row(['Sabor', 'Custo R$/kg', 'Preço R$/un', 'Preço R$/kg', ...MESES, 'Total 12m']),
  );

  const porSabor = card.vendas.por_sabor ?? [];
  const byFlavor = new Map(porSabor.map((s) => [s.flavorId, s]));
  const alvo = flavors.length ? flavors : porSabor.map((s) => ({ id: s.flavorId, name: s.nome ?? '—' } as FlavorRow));
  for (const f of alvo) {
    const s = byFlavor.get(f.id);
    const vols = s?.volume_mensal ?? [];
    const total = vols.reduce((a, b) => a + (b || 0), 0);
    lines.push(
      row([
        f.name,
        s?.custo_kg ?? '',
        s?.preco_un ?? '',
        s?.preco_kg ?? '',
        ...MESES.map((_, i) => vols[i] ?? 0),
        total,
      ]),
    );
  }

  const csv = '﻿' + lines.join('\r\n'); // BOM p/ acentos no Excel
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `capa-${slug(product.brand)}-${slug(product.name)}.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

function fmt(iso: string | null): string {
  if (!iso) return '—';
  const [y, m, d] = iso.slice(0, 10).split('-');
  return `${d}/${m}/${y}`;
}
function slug(s: string): string {
  return s
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-zA-Z0-9]+/g, '-')
    .toLowerCase()
    .replace(/^-+|-+$/g, '');
}
